import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./RolePermissions.css";

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const API =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (isLocalhost ? "http://localhost:5003" : "https://erp-api.sdcllp.co.in");

const authHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("softvolute_token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// Stable string for "is there any change" comparison.
const normalize = (permissions = {}) =>
  JSON.stringify(
    Object.keys(permissions)
      .filter((key) => (permissions[key] || []).length)
      .sort()
      .map((key) => [key, [...permissions[key]].sort()]),
  );

const RolePermissions = () => {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [draft, setDraft] = useState({});

  const [search, setSearch] = useState("");
  const [newRole, setNewRole] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedRole = useMemo(
    () => roles.find((role) => role.name === selectedName) || null,
    [roles, selectedName],
  );

  const dirty = selectedRole
    ? normalize(draft) !== normalize(selectedRole.permissions)
    : false;

  const loadData = async (preferredName) => {
    try {
      setLoading(true);
      setError("");

      const [catalogResponse, rolesResponse] = await Promise.all([
        axios.get(`${API}/api/role-permissions/catalog`, {
          headers: authHeaders(),
        }),
        axios.get(`${API}/api/role-permissions/roles`, {
          headers: authHeaders(),
        }),
      ]);

      const roleList = rolesResponse.data?.data || [];

      setCatalog(catalogResponse.data?.data || []);
      setRoles(roleList);

      const nextName =
        roleList.find((role) => role.name === preferredName)?.name ||
        roleList[0]?.name ||
        "";

      setSelectedName(nextName);
      setDraft(
        JSON.parse(
          JSON.stringify(
            roleList.find((role) => role.name === nextName)?.permissions || {},
          ),
        ),
      );
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to load roles."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredRoles = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return roles;
    return roles.filter((role) => role.name.toLowerCase().includes(value));
  }, [roles, search]);

  const selectRole = (role) => {
    if (role.name === selectedName) return;

    if (dirty && !window.confirm("Discard unsaved permission changes?")) return;

    setSelectedName(role.name);
    setDraft(JSON.parse(JSON.stringify(role.permissions || {})));
    setError("");
    setSuccess("");
  };

  const locked = Boolean(selectedRole?.locked);

  const isChecked = (pageKey, action) =>
    (draft[pageKey] || []).includes(action);

  const toggleAction = (page, action) => {
    if (locked) return;

    setDraft((current) => {
      const actions = new Set(current[page.key] || []);

      if (actions.has(action)) {
        if (action === "view") {
          actions.clear();
        } else {
          actions.delete(action);
        }
      } else {
        actions.add(action);
        if (action !== "view" && page.actions.includes("view")) {
          actions.add("view");
        }
      }

      return { ...current, [page.key]: [...actions] };
    });
  };

  const togglePage = (page) => {
    if (locked) return;

    setDraft((current) => {
      const all = (current[page.key] || []).length === page.actions.length;
      return { ...current, [page.key]: all ? [] : [...page.actions] };
    });
  };

  const savePermissions = async () => {
    if (!selectedRole || locked) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await axios.put(
        `${API}/api/role-permissions/roles/${encodeURIComponent(
          selectedRole.name,
        )}/permissions`,
        { permissions: draft },
        { headers: authHeaders() },
      );

      setSuccess(`Permissions saved for ${selectedRole.name}.`);
      await loadData(selectedRole.name);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to save permissions."));
    } finally {
      setSaving(false);
    }
  };

  const addRole = async (event) => {
    event.preventDefault();

    const name = newRole.trim();
    if (!name) return;

    try {
      setAdding(true);
      setError("");
      setSuccess("");

      await axios.post(
        `${API}/api/role-permissions/roles`,
        { name },
        { headers: authHeaders() },
      );

      setNewRole("");
      setSuccess(`Role "${name}" created.`);
      await loadData(name);
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Unable to create role."));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="rp-page">
      {/* LEFT: ROLES */}
      <aside className="rp-roles">
        <h1>Roles</h1>
        <p className="rp-sub">Select role and assign permissions</p>

        <form className="rp-add" onSubmit={addRole}>
          <input
            placeholder="Create new role"
            value={newRole}
            onChange={(event) => setNewRole(event.target.value)}
          />
          <button type="submit" disabled={adding || !newRole.trim()}>
            {adding ? "..." : "+ Add"}
          </button>
        </form>

        <input
          className="rp-search"
          placeholder="Search role..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className="rp-role-list">
          {filteredRoles.map((role, index) => (
            <button
              type="button"
              key={role.name}
              className={`rp-role-item ${
                role.name === selectedName ? "active" : ""
              }`}
              onClick={() => selectRole(role)}
            >
              <span className="rp-role-number">{index + 1}</span>

              <span className="rp-role-text">
                <strong>
                  {role.name}
                  {role.locked && <em className="rp-locked-tag">LOCKED</em>}
                </strong>
                <small>{role.status}</small>
              </span>

              <span className="rp-chevron">›</span>
            </button>
          ))}

          {!loading && !filteredRoles.length && (
            <div className="rp-empty">No roles found.</div>
          )}
        </div>
      </aside>

      {/* RIGHT: DETAILS */}
      <section className="rp-details">
        {/* FROZEN AREA: header + alerts + role banner */}
        <div className="rp-sticky">
          <header className="rp-details-head">
            <div>
              <h1>Role Details</h1>
              <p className="rp-sub">
                {selectedRole
                  ? `Manage access for ${selectedRole.name}`
                  : "Select a role"}
              </p>
            </div>

            <button
              type="button"
              className="rp-save"
              disabled={!dirty || saving || locked}
              onClick={savePermissions}
            >
              {saving ? "Saving..." : "Save Permissions"}
            </button>
          </header>

          {error && <div className="rp-alert rp-alert-error">{error}</div>}
          {success && (
            <div className="rp-alert rp-alert-success">{success}</div>
          )}

          {!loading && selectedRole && (
            <div className="rp-banner">
              <div>
                <h2>{selectedRole.name}</h2>
                <p>
                  {locked
                    ? `${selectedRole.name} has full default access. It cannot be changed.`
                    : `Choose which pages and actions ${selectedRole.name} can use.`}
                </p>
              </div>

              {locked && (
                <span className="rp-banner-badge">Locked Full Access</span>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="rp-empty">Loading roles...</div>
        ) : (
          selectedRole && (
            <div className="rp-panel">
              <div className="rp-panel-head">
                <h3>
                  <span className="rp-panel-icon">👤</span>
                  USER PAGE PERMISSIONS
                </h3>
                <span className="rp-pill">{catalog.length} Pages</span>
              </div>

              <div className="rp-grid">
                {catalog.map((page) => {
                  const selectedCount = (draft[page.key] || []).length;
                  const allSelected = selectedCount === page.actions.length;

                  return (
                    <div className="rp-card" key={page.key}>
                      <div className="rp-card-head">
                        <label className="rp-check">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            disabled={locked}
                            onChange={() => togglePage(page)}
                          />
                          <span>{page.label}</span>
                        </label>

                        <span className="rp-count">
                          {selectedCount}/{page.actions.length}
                        </span>
                      </div>

                      <div className="rp-actions">
                        {page.actionLabels.map((action) => {
                          const checked = isChecked(page.key, action.key);

                          return (
                            <label
                              key={action.key}
                              className={`rp-chip ${checked ? "on" : ""} ${
                                locked ? "disabled" : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={locked}
                                onChange={() => toggleAction(page, action.key)}
                              />
                              <span>{action.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </section>
    </div>
  );
};

export default RolePermissions;
